/**
 * Dialog "Sincronizar páginas" — picker de páginas por conta Meta.
 *
 * Em vez de puxar tudo que /me/accounts devolve (que inclui páginas
 * compartilhadas via Business Manager de terceiros), abre um modal listando
 * só as páginas que o usuário concedeu no OAuth, com checkbox pra escolher
 * quais realmente vincular — sem precisar refazer login. Avisa quando uma
 * página já está em outra conta (vincular aqui = mover).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, Search, AlertTriangle, Check } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { previewSyncMetaPages, commitSyncMetaPages } from '@/services/api/connections';
import { MetaUser } from '../types/meta.types';

interface Props {
    /** Conta a sincronizar. null = dialog fechado. */
    auth: MetaUser | null;
    onClose: () => void;
    onSynced?: () => void;
}

export default function SyncPagesDialog({ auth, onClose, onSynced }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const open = auth != null;

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['page-sync-preview', auth?.id],
        queryFn: () => previewSyncMetaPages({
            meta_user_id: auth!.id,
            meta_app_id: auth!.meta_app_id,
        }),
        enabled: open,
        staleTime: 0,
        refetchOnWindowFocus: false,
    });

    // Quando o preview chega, inicializa a seleção com o que o backend sugeriu.
    useEffect(() => {
        if (data?.pages) {
            setSelected(new Set(data.pages.filter((p) => p.selected).map((p) => p.page_id)));
        }
    }, [data]);

    useEffect(() => {
        if (open) setSearch('');
    }, [open]);

    const pages = data?.pages ?? [];
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q
            ? pages.filter((p) => (p.name || '').toLowerCase().includes(q) || p.page_id.includes(q))
            : pages;
    }, [pages, search]);

    const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.page_id));
    const toggleAll = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allFilteredSelected) filtered.forEach((p) => next.delete(p.page_id));
            else filtered.forEach((p) => next.add(p.page_id));
            return next;
        });
    };
    const toggle = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Selecionadas que também estão em OUTRO perfil (nome diferente) — possível vínculo de terceiro.
    const otherProfileSelected = useMemo(
        () => pages.filter(
            (p) => selected.has(p.page_id) && p.also_in.some((l) => l.different_profile),
        ).length,
        [pages, selected],
    );

    const commitMut = useMutation({
        mutationFn: () => commitSyncMetaPages({
            meta_user_id: auth!.id,
            meta_app_id: auth!.meta_app_id,
            page_ids: Array.from(selected),
        }),
        onSuccess: (r) => {
            const parts: string[] = [];
            if (r.created) parts.push(`${r.created} novas`);
            if (r.updated) parts.push(`${r.updated} atualizadas`);
            if (r.migrated) parts.push(`${r.migrated} migradas`);
            if (r.linked) parts.push(`${r.linked} vinculadas a este app`);
            if (r.deactivated) parts.push(`${r.deactivated} desativadas`);
            toast({
                title: 'Páginas sincronizadas!',
                description: parts.length ? parts.join(' · ') + '.' : 'Nada mudou.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            queryClient.invalidateQueries({ queryKey: ['meta-users'] });
            onSynced?.();
            onClose();
        },
        onError: (e: any) => toast({
            variant: 'destructive',
            title: 'Erro ao sincronizar',
            description: e?.response?.data?.detail || e?.message,
        }),
    });

    const hiddenCount = data && data.restricted_by_grant
        ? Math.max(0, data.total_accessible - data.total_candidates)
        : 0;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !commitMut.isPending) onClose(); }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
                <DialogHeader className="min-w-0">
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 text-blue-600 shrink-0" /> Sincronizar páginas
                    </DialogTitle>
                    <DialogDescription>
                        {auth?.meta_user_name ? (
                            <>Escolha quais páginas de <span className="font-medium">{auth.meta_user_name}</span> vincular a esta conta.</>
                        ) : 'Escolha quais páginas vincular a esta conta.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 min-w-0">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-500">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                            <span className="text-sm">Buscando páginas no Facebook…</span>
                        </div>
                    ) : isError ? (
                        <p className="text-sm text-red-600 py-6 text-center">
                            {(error as any)?.response?.data?.detail || 'Erro ao buscar páginas.'}
                        </p>
                    ) : pages.length === 0 ? (
                        <p className="text-sm text-gray-400 italic py-8 text-center">
                            Nenhuma página disponível pra esta conta.
                        </p>
                    ) : (
                        <>
                            <div className="flex items-center gap-2">
                                <div className="relative flex-1 min-w-0">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <Input
                                        value={search} onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Buscar página..." className="pl-8 h-9"
                                    />
                                </div>
                                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer whitespace-nowrap">
                                    <Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} />
                                    Todas
                                </label>
                            </div>

                            {hiddenCount > 0 && (
                                <p className="text-[11px] text-gray-400">
                                    {hiddenCount} página(s) que você não selecionou no login foram ocultadas.
                                </p>
                            )}

                            {otherProfileSelected > 0 && (
                                <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>
                                        {otherProfileSelected} página(s) marcada(s) também estão em <strong>outro perfil</strong> —
                                        confira se são mesmo desta conta antes de sincronizar.
                                    </span>
                                </div>
                            )}

                            <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
                                {filtered.map((p) => {
                                    const sel = selected.has(p.page_id);
                                    return (
                                        <button
                                            key={p.page_id} type="button" onClick={() => toggle(p.page_id)}
                                            className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                                                sel ? 'bg-blue-50' : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className={`flex h-4 w-4 items-center justify-center rounded border shrink-0 ${
                                                sel ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                                            }`}>
                                                {sel && <Check className="h-3 w-3 text-white" />}
                                            </div>
                                            {p.picture_url ? (
                                                <img src={p.picture_url} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                                            ) : (
                                                <div className="w-7 h-7 rounded-full bg-gray-200 shrink-0" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{p.name || p.page_id}</p>
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    {p.already_synced && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                                            já sincronizada
                                                        </span>
                                                    )}
                                                    {p.also_in.map((l, i) => (
                                                        <span
                                                            key={i}
                                                            title={l.different_profile
                                                                ? `Também vinculada no perfil ${l.meta_user_name || ''} (${l.app_name || ''})`
                                                                : `Mesma página em outro app (${l.app_name || ''})`}
                                                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                                                                l.different_profile
                                                                    ? 'bg-amber-100 text-amber-700'
                                                                    : 'bg-gray-100 text-gray-500'
                                                            }`}
                                                        >
                                                            {l.different_profile
                                                                ? `também em ${l.meta_user_name || 'outro perfil'}`
                                                                : `também em ${l.app_name || 'outro app'}`}
                                                        </span>
                                                    ))}
                                                    {p.category && !p.already_synced && p.also_in.length === 0 && (
                                                        <span className="text-[10px] text-gray-400 truncate">{p.category}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={commitMut.isPending}>
                        Cancelar
                    </Button>
                    <Button onClick={() => commitMut.mutate()} disabled={isLoading || isError || commitMut.isPending}>
                        {commitMut.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                        Sincronizar {selected.size > 0 ? `(${selected.size})` : ''}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
